import { useTheme } from '@emotion/react'
import { authEmailToken, ConfigurationContext } from '@news-mono/web-common'
import React, { useContext, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router'
import { Link } from 'react-router-dom'
import { Skeleton } from '../../content/Skeleton/Skeleton'
import { useAuthActions } from '../../user-registration/authentication/AuthenticationProvider'
import { themedValue } from '../../__styling'
import { MastheadPreference } from '../components/MastheadPreference'
import { EmailPreferenceItem } from '../components/PreferenceSection'
import {
    StyledPerthNowLogo,
    StyledTheWestLogo,
} from '../components/PreferenceSection.styled'
import { getMastheadPreferences } from '../config'
import { PreferenceCardContext } from '../contexts/PreferenceCardContext'
import { EPC_STORAGE_KEY } from '../EmailPreference'
import {
    StyledBody,
    StyledEmail,
    StyledEmailPreference,
    StyledFooter,
    StyledGreeting,
    StyledHeading,
    StyledWebLink,
} from '../EmailPreference.styled'
import {
    authClickHandler,
    useEmailPreferenceData,
    useGetPreferences,
} from '../hooks/useEmailPreferenceData'
import { useResolveEmail } from '../hooks/useUnsubscribe'

export const EmailPreferenceWithToken = () => {
    const theme = useTheme()
    const location = useLocation()
    const dispatch = useDispatch()
    const { emailServiceApi } = useContext(ConfigurationContext)

    const queryParams = new URLSearchParams(location.search)
    const token = queryParams.get('token')
    const {
        data: emailData,
        isLoading: isEmailLoading,
        resolveEmailError,
    } = useResolveEmail(false, emailServiceApi, token ?? undefined, undefined)
    const { onLoginClick, onRegisterClick } = useAuthActions()

    useEffect(() => {
        dispatch(
            authEmailToken({
                emailToken: token ? token : '',
            }),
        )
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const {
        isLoading: isPreferenceLoading,
        isError: isPreferenceError,
        data: preferenceData,
        error: preferenceError,
    } = useEmailPreferenceData(token ? token : '')

    if (isPreferenceError) {
        throw new Error(preferenceError as string)
    }

    const mastheadPreferences = useGetPreferences(
        preferenceData?.result,
        getMastheadPreferences(theme),
    )

    const meta = themedValue(theme, {
        thewest: {
            logoGroup: [<StyledTheWestLogo />, <StyledPerthNowLogo />],
            contactLink: '/contact',
        },
        perthnow: {
            logoGroup: [<StyledPerthNowLogo />, <StyledTheWestLogo />],
            contactLink: '/contact-us',
        },
        sevennews: {
            logoGroup: [],
            contactLink: '/contact-us',
        },
    })

    const ToggleHandler = async (
        checked: boolean,
        item: EmailPreferenceItem,
    ): Promise<void> => {
        const api = `${emailServiceApi}/email-preference-center/toggle-preference`
        const response = await fetch(api, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: token ? token : '',
            },
            body: JSON.stringify({
                account: item.subAccount,
                listId: item.guid,
                toggleState: item.optIn ? 'off' : 'on',
                unsubscribeGroupId: item.unSubId,
            }),
        })

        if (!response.ok) {
            throw new Error('Network response was not ok')
        }
    }

    return (
        <StyledEmailPreference>
            <StyledHeading>Email Preferences</StyledHeading>
            <StyledGreeting>
                You're updating preferences for:{' '}
                {isEmailLoading ? (
                    <Skeleton width={300} height={20} />
                ) : (
                    emailData.tokenEmail
                )}
            </StyledGreeting>
            <StyledEmail>
                Email address incorrect?{' '}
                <Link
                    onClick={(e) =>
                        authClickHandler(e, EPC_STORAGE_KEY, onLoginClick)
                    }
                    to={''}
                >
                    Login
                </Link>{' '}
                or{' '}
                <Link
                    onClick={(e) =>
                        authClickHandler(e, EPC_STORAGE_KEY, onRegisterClick)
                    }
                    to={''}
                >
                    Register
                </Link>
                .
            </StyledEmail>
            <StyledBody>
                Pick and choose the news that you want delivered to your inbox.
            </StyledBody>
            {mastheadPreferences.map((preferences, index) => (
                <PreferenceCardContext.Provider
                    value={{
                        toggleHandler: ToggleHandler,
                    }}
                    key={index}
                >
                    <MastheadPreference
                        preferences={preferences}
                        logo={meta.logoGroup[index]}
                        visitorType={'Anonymous'}
                        isLoading={isPreferenceLoading}
                    />
                </PreferenceCardContext.Provider>
            ))}
            <StyledFooter>
                <h2>Looking for something else?</h2>
                <StyledWebLink to={meta.contactLink}>Contact us</StyledWebLink>
                <StyledWebLink
                    to={`/manage-email-preferences/unsubscribe/all?token=${token}`}
                >
                    Unsubscribe from all promos and marketing
                </StyledWebLink>
            </StyledFooter>
        </StyledEmailPreference>
    )
}
